import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Sun, Moon, Monitor, Type } from "lucide-react";
import {
  getStoredTheme,
  setTheme as applyStoredTheme,
  getStoredSize,
  setTextSize as applyStoredSize,
} from "../lib/theme";

const ThemeToggle = () => {
  const [theme, setThemeState] = useState("system");
  const [size, setSizeState] = useState("md");

  useEffect(() => {
    setThemeState(getStoredTheme());
    setSizeState(getStoredSize());
  }, []);

  const handleTheme = (value) => {
    setThemeState(value);
    applyStoredTheme(value);
  };

  const handleSize = (value) => {
    setSizeState(value);
    applyStoredSize(value);
  };

  return (
    <Card data-testid="theme-toggle-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sun className="h-4 w-4 text-amber-500 dark:hidden" />
          <Moon className="h-4 w-4 text-indigo-400 hidden dark:inline-block" />
          Videz aplikacije
        </CardTitle>
        <CardDescription>
          Prilagodite temo in velikost besedila za boljšo berljivost.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Theme */}
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium">Tema</p>
          <RadioGroup
            value={theme}
            onValueChange={handleTheme}
            className="grid grid-cols-3 gap-2"
          >
            <label
              htmlFor="theme-light"
              className={`cursor-pointer flex flex-col items-center gap-1 rounded-lg border p-3
                ${theme === "light" ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="theme-option-light"
            >
              <RadioGroupItem value="light" id="theme-light" className="sr-only" />
              <Sun className="h-5 w-5 text-amber-500" />
              <span className="text-xs font-medium">Svetla</span>
            </label>
            <label
              htmlFor="theme-dark"
              className={`cursor-pointer flex flex-col items-center gap-1 rounded-lg border p-3
                ${theme === "dark" ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="theme-option-dark"
            >
              <RadioGroupItem value="dark" id="theme-dark" className="sr-only" />
              <Moon className="h-5 w-5 text-indigo-500" />
              <span className="text-xs font-medium">Temna</span>
            </label>
            <label
              htmlFor="theme-system"
              className={`cursor-pointer flex flex-col items-center gap-1 rounded-lg border p-3
                ${theme === "system" ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="theme-option-system"
            >
              <RadioGroupItem value="system" id="theme-system" className="sr-only" />
              <Monitor className="h-5 w-5 text-muted-foreground" />
              <span className="text-xs font-medium">Sistem</span>
            </label>
          </RadioGroup>
        </div>

        {/* Text size */}
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
            <Type className="h-3.5 w-3.5" />
            Velikost besedila
          </p>
          <RadioGroup
            value={size}
            onValueChange={handleSize}
            className="grid grid-cols-4 gap-2"
          >
            <label
              htmlFor="size-sm"
              className={`cursor-pointer flex flex-col items-center gap-1 rounded-lg border p-3
                ${size === "sm" ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="text-size-sm"
            >
              <RadioGroupItem value="sm" id="size-sm" className="sr-only" />
              <span className="font-semibold" style={{ fontSize: "12px" }}>A</span>
              <span className="text-[10px] font-medium text-muted-foreground">Manjše</span>
            </label>
            <label
              htmlFor="size-md"
              className={`cursor-pointer flex flex-col items-center gap-1 rounded-lg border p-3
                ${size === "md" ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="text-size-md"
            >
              <RadioGroupItem value="md" id="size-md" className="sr-only" />
              <span className="font-semibold" style={{ fontSize: "15px" }}>A</span>
              <span className="text-[10px] font-medium text-muted-foreground">Privzeto</span>
            </label>
            <label
              htmlFor="size-lg"
              className={`cursor-pointer flex flex-col items-center gap-1 rounded-lg border p-3
                ${size === "lg" ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="text-size-lg"
            >
              <RadioGroupItem value="lg" id="size-lg" className="sr-only" />
              <span className="font-semibold" style={{ fontSize: "18px" }}>A</span>
              <span className="text-[10px] font-medium text-muted-foreground">Veliko</span>
            </label>
            <label
              htmlFor="size-xl"
              className={`cursor-pointer flex flex-col items-center gap-1 rounded-lg border p-3
                ${size === "xl" ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="text-size-xl"
            >
              <RadioGroupItem value="xl" id="size-xl" className="sr-only" />
              <span className="font-semibold" style={{ fontSize: "22px" }}>A</span>
              <span className="text-[10px] font-medium text-muted-foreground">Zelo veliko</span>
            </label>
          </RadioGroup>
        </div>
      </CardContent>
    </Card>
  );
};

export default ThemeToggle;
